export type Status = 'idle' | 'connecting' | 'live' | 'speaking' | 'error';

export type Role = 'user' | 'merlin';

interface Elements {
  dot: HTMLElement;
  statusText: HTMLElement;
  latency: HTMLElement;
  banner: HTMLElement;
  mic: HTMLButtonElement;
  micLabel: HTMLElement;
  mute: HTMLButtonElement;
  muteLabel: HTMLElement;
  end: HTMLButtonElement;
  toggle: HTMLButtonElement;
  closeDrawer: HTMLButtonElement;
  drawer: HTMLElement;
  transcript: HTMLElement;
  caption: HTMLElement;
  captionText: HTMLElement;
  captionWho: HTMLElement;
  orbSigil: HTMLElement;
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing element: #${id}`);
  return node as T;
}

/**
 * Tracks the streaming transcript for one role.
 *
 * Handles two realities at once:
 *   1. Realtime providers may send deltas or cumulative snapshots. If a chunk
 *      starts with what we've already accumulated, we treat it as a
 *      snapshot and replace.
 *   2. Delta chunks from the provider are not reliably space-delimited —
 *      "Asif." and "To" arrive back-to-back with no whitespace between
 *      them. We preserve every space the provider sends, and inject a
 *      single space at join points where two word characters would
 *      otherwise mash together (but not before punctuation).
 */
class TurnBuffer {
  text = '';

  push(chunk: string): string {
    if (!chunk) return this.text;

    // Cumulative snapshot — replace.
    if (this.text && chunk.startsWith(this.text)) {
      this.text = chunk;
      return this.text;
    }
    // Duplicate tail — ignore (provider re-sent the last chunk).
    if (this.text && this.text.endsWith(chunk)) return this.text;

    // Delta — append. Insert a space only if the boundary would create
    // a word collision. Don't insert one in front of punctuation.
    const prevEndsWithSpace = /\s$/.test(this.text);
    const chunkStartsWithSpaceOrPunct = /^[\s.,!?;:)\]}'"…—–-]/.test(chunk);
    const needsSpace =
      this.text.length > 0 && !prevEndsWithSpace && !chunkStartsWithSpaceOrPunct;
    this.text = needsSpace ? `${this.text} ${chunk}` : this.text + chunk;
    return this.text;
  }

  replace(full: string) {
    this.text = full;
  }

  clear() {
    this.text = '';
  }
}

export class UI {
  private readonly els: Elements;
  private readonly buffers: Record<Role, TurnBuffer> = {
    user: new TurnBuffer(),
    merlin: new TurnBuffer(),
  };
  private captionRole: Role = 'merlin';
  private currentStatus: Status = 'idle';
  /**
   * Sticky while a call is live: once muted, the "Listening" status reads
   * "Muted" across activity flips (speaking/listening) until unmuted.
   * "Merlin speaking" is never overridden by this flag.
   */
  private muted = false;

  constructor() {
    this.els = {
      dot: el('status-dot'),
      statusText: el('status-text'),
      latency: el('latency'),
      banner: el('banner'),
      mic: el<HTMLButtonElement>('mic'),
      micLabel: el('mic').querySelector('.btn-label') as HTMLElement,
      mute: el<HTMLButtonElement>('mute'),
      muteLabel: el('mute').querySelector('.btn-label') as HTMLElement,
      end: el<HTMLButtonElement>('end'),
      toggle: el<HTMLButtonElement>('toggle-transcript'),
      closeDrawer: el<HTMLButtonElement>('close-drawer'),
      drawer: el('drawer'),
      transcript: el('transcript'),
      caption: el('caption'),
      captionText: el('caption-text'),
      captionWho: el('caption-who'),
      orbSigil: el('orb-sigil'),
    };
    this.els.toggle.addEventListener('click', () => this.toggleDrawer());
    this.els.closeDrawer.addEventListener('click', () => this.closeDrawer());
  }

  setStatus(status: Status, text?: string) {
    this.currentStatus = status;
    this.els.dot.classList.remove('live', 'speaking', 'error', 'connecting');
    if (status === 'live') this.els.dot.classList.add('live');
    if (status === 'speaking') this.els.dot.classList.add('speaking');
    if (status === 'error') this.els.dot.classList.add('error');
    if (status === 'connecting') this.els.dot.classList.add('connecting');
    const labels: Record<Status, string> = {
      idle: 'Ready',
      connecting: 'Connecting',
      live: this.muted ? 'Muted' : 'Listening',
      speaking: 'Merlin speaking',
      error: 'Error',
    };
    this.els.statusText.textContent = text ?? labels[status];
    this.els.orbSigil.classList.toggle('speaking', status === 'speaking');
    this.els.caption.classList.toggle('active', status === 'speaking' || status === 'live');
  }

  /** Provider name is intentionally not surfaced — internal only. */
  setProvider(_provider: string) {
    // no-op: hide model identity from end users
  }

  setLatency(ms: number) {
    this.els.latency.textContent = `${ms} ms`;
  }
  clearLatency() {
    this.els.latency.textContent = '—';
  }

  onMicClick(handler: () => void) {
    this.els.mic.addEventListener('click', handler);
  }
  onMuteClick(handler: () => void) {
    this.els.mute.addEventListener('click', handler);
  }
  onEndClick(handler: () => void) {
    this.els.end.addEventListener('click', handler);
  }

  /** Shows/hides the mute control. Intake-Gemini-only, wired by the caller. */
  setMuteAvailable(available: boolean) {
    this.els.mute.hidden = !available;
  }

  /** Sets the pressed/label state and the sticky "Muted" status override. */
  setMuted(muted: boolean) {
    this.muted = muted;
    this.els.mute.setAttribute('aria-pressed', String(muted));
    this.els.mute.setAttribute('aria-label', muted ? 'Unmute microphone' : 'Mute microphone');
    this.els.muteLabel.textContent = muted ? 'Unmute' : 'Mute';
    if (this.currentStatus === 'live') this.setStatus('live');
  }

  setCallActive(active: boolean) {
    this.els.mic.disabled = active;
    this.els.end.disabled = !active;
    this.els.micLabel.textContent = active ? 'In call' : 'Begin call';
    if (!active) {
      this.resetCaption();
      this.setMuted(false);
      this.setMuteAvailable(false);
    }
  }

  showBanner(message: string, variant: 'info' | 'error' = 'info') {
    this.els.banner.textContent = message;
    this.els.banner.classList.toggle('error', variant === 'error');
    this.els.banner.classList.add('show');
  }
  hideBanner() {
    this.els.banner.classList.remove('show', 'error');
    this.els.banner.textContent = '';
  }

  resetCaption() {
    this.buffers.merlin.clear();
    this.buffers.user.clear();
    this.els.transcript.replaceChildren();
    this.closeDrawer();
    this.els.captionText.textContent = '';
    this.els.captionText.classList.add('idle');
    this.setCaptionRole('merlin');
  }

  setCaptionIntro(text: string) {
    this.els.captionText.classList.remove('idle');
    this.els.captionText.textContent = text;
    this.setCaptionRole('merlin');
  }

  /**
   * Streaming transcript update. Drives the live caption and, on final,
   * emits a completed turn into the history drawer.
   */
  pushTranscript(role: Role, chunk: string, final: boolean) {
    // Don't trim the chunk — the provider's whitespace is part of the
    // stream. Trimming here strips the spaces that sit between chunks
    // and produces garbage like "Asif.To ensureI spellit".
    if (!chunk && !final) return;

    const buffer = this.buffers[role];
    if (chunk) buffer.push(chunk);

    // The live caption is Merlin-only — user speech still goes into the
    // transcript drawer history on commit, but it never overwrites or
    // pollutes the caption panel.
    if (role === 'merlin') {
      if (this.captionRole !== role) {
        this.commitTurn(this.captionRole);
        this.setCaptionRole(role);
      }
      this.els.captionText.classList.remove('idle');
      // Trim only for display — keep the buffer raw so commit / join maths stay correct.
      this.els.captionText.textContent = buffer.text.trim();
    }

    if (final) {
      this.commitTurn(role);
    }
  }

  /** Commits the live caption's current content into the transcript drawer. */
  commitTurn(role: Role) {
    const buffer = this.buffers[role];
    const text = buffer.text.trim();
    if (!text) return;
    this.appendHistory(role, text);
    buffer.clear();
  }

  private setCaptionRole(role: Role) {
    this.captionRole = role;
    this.els.captionWho.textContent = role === 'merlin' ? 'Merlin' : 'You';
    this.els.captionWho.classList.toggle('you', role === 'user');
  }

  private appendHistory(role: Role, text: string) {
    const turn = document.createElement('div');
    turn.className = `turn ${role}`;
    const who = document.createElement('div');
    who.className = 'who';
    who.textContent = role === 'merlin' ? 'Merlin' : 'You';
    const body = document.createElement('div');
    body.className = 'text';
    body.textContent = text;
    turn.appendChild(who);
    turn.appendChild(body);
    this.els.transcript.appendChild(turn);
    this.els.transcript.scrollTop = this.els.transcript.scrollHeight;
  }

  private toggleDrawer() {
    this.els.drawer.classList.toggle('open');
  }
  closeDrawer() {
    this.els.drawer.classList.remove('open');
  }
  get isDrawerOpen(): boolean {
    return this.els.drawer.classList.contains('open');
  }
}
